import { http } from 'core/utils/httpClient';
import type { Document } from 'models/document/document';
import type { User } from 'models/user/user';
import type { PaginatedList } from 'services/dtoModels/paginatedList';
import type { Paginator } from 'services/dtoModels/paginator';
import type { CreateUserRequest } from 'services/dtoModels/user/createUserRequest';
import type { GetUsersRequest } from 'services/dtoModels/user/getUsersRequest';
import type { UpdateUserRequest } from 'services/dtoModels/user/updateUserRequest';
import type { UserResponse } from 'services/dtoModels/user/userResponse';

/**
 * Нормализует ответ API: приводит nullable mango-поля к строке
 *
 * @param response  User response
 * @returns Normalized user
 */
const normalizeUser = (response: UserResponse): User => ({
  ...response,
  mangoExtension: response.mangoExtension ?? '',
});

export class UserService {
  async getUsersAsync(
    request: GetUsersRequest & Paginator
  ): Promise<PaginatedList<User>> {
    const { data } = await http.get<PaginatedList<UserResponse>>('users', {
      params: request,
    });

    return {
      ...data,
      items: data.items.map(normalizeUser),
    };
  }

  async getUserAsync(userId: number): Promise<User> {
    const { data } = await http.get<UserResponse>(`users/${userId}`);
    return normalizeUser(data);
  }

  async updateUserAsync(userId: number, request: UpdateUserRequest): Promise<User> {
    const { data } = await http.put<UserResponse>(`users/${userId}`, request);
    return normalizeUser(data);
  }

  async createUserAsync(request: CreateUserRequest): Promise<User> {
    const { data } = await http.post<UserResponse>('users', request);
    return normalizeUser(data);
  }

  async getDocumentsAsync(userId: number): Promise<Document[]> {
    const { data } = await http.get<Document[]>(`users/${userId}/documents`);
    return data;
  }

  async putDocumentAccessesAsync(userId: number, documentIds: number[]): Promise<void> {
    await http.put(`users/${userId}/documents`, documentIds);
  }
}

const userService = new UserService();
export default userService;
